/**
 * AppCacheContext
 *
 * Provides app-level caching for org-level data to minimize API calls.
 * Caches: user permissions
 */

import React, { createContext, useContext, useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useAuth } from '../auth/auth0-provider';
import { api } from '../api/sdk';
import { isApiClientError } from '../api/errors';

// Cache TTL in milliseconds
const CACHE_TTL = {
  userPermissions: 10 * 60 * 1000, // 10 minutes
};

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface UserPermissionsData {
  role: string | null;
  permissions: string[];
  organizationId: string | null;
}

// Response shape of GET /api/auth/me
interface AuthMeResponse {
  role?: string | null;
  permissions?: string[];
  activeOrganization?: { id: string } | null;
}

interface AppCacheContextValue {
  // User Permissions (fetched from backend based on role in database)
  userPermissions: string[];
  userRole: string | null;
  hasPermission: (permission: string) => boolean;
  permissionsLoading: boolean;
  refreshPermissions: () => Promise<void>;
}

const AppCacheContext = createContext<AppCacheContextValue | null>(null);

export function AppCacheProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, isSdkReady } = useAuth();

  // User permissions state (needs to trigger re-renders when updated)
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [permissionsLoadingInternal, setPermissionsLoadingInternal] = useState(true);

  // Permissions are loading if Auth0 is loading OR we're fetching permissions
  const permissionsLoading = authLoading || permissionsLoadingInternal;

  // Cache refs (persist across renders without causing re-renders)
  const permissionsCache = useRef<CacheEntry<UserPermissionsData> | null>(null);

  // In-flight request tracking to prevent duplicate concurrent requests
  const inFlightPermissions = useRef<Promise<UserPermissionsData | null> | null>(null);

  // Check if cache entry is valid
  const isCacheValid = <T,>(entry: CacheEntry<T> | null | undefined, ttl: number): boolean => {
    if (!entry) return false;
    return Date.now() - entry.timestamp < ttl;
  };

  // Fetch user permissions from backend (based on role in database)
  const fetchUserPermissions = useCallback(async (): Promise<UserPermissionsData | null> => {
    // Return cached data if valid
    if (isCacheValid(permissionsCache.current, CACHE_TTL.userPermissions)) {
      return permissionsCache.current!.data;
    }

    // Return in-flight request if exists
    if (inFlightPermissions.current) {
      return inFlightPermissions.current;
    }

    const fetchPromise = (async () => {
      const delays = [0, 500, 1500, 3000];
      let lastError: unknown = null;

      for (const delay of delays) {
        if (delay > 0) {
          await new Promise((r) => setTimeout(r, delay));
        }
        try {
          const result = await api.get<AuthMeResponse>('/api/auth/me');

          const permissionsData: UserPermissionsData = {
            role: result.role || null,
            permissions: result.permissions || [],
            organizationId: result.activeOrganization?.id || null,
          };

          permissionsCache.current = {
            data: permissionsData,
            timestamp: Date.now(),
          };

          inFlightPermissions.current = null;
          return permissionsData;
        } catch (error) {
          lastError = error;
          // Don't retry definitive failures — 401/403 won't resolve by waiting.
          if (isApiClientError(error) && (error.isForbidden() || error.isAuthError())) {
            break;
          }
        }
      }

      console.error('Error fetching user permissions after retries:', lastError);
      inFlightPermissions.current = null;
      return permissionsCache.current?.data || null;
    })();

    inFlightPermissions.current = fetchPromise;
    return fetchPromise;
  }, []);

  // Refresh permissions and update state
  const refreshPermissions = useCallback(async () => {
    setPermissionsLoadingInternal(true);
    permissionsCache.current = null; // Clear cache to force refresh
    inFlightPermissions.current = null; // Drop any in-flight request started pre-clear
    const data = await fetchUserPermissions();
    if (data) {
      setUserPermissions(data.permissions);
      setUserRole(data.role);
    }
    setPermissionsLoadingInternal(false);
  }, [fetchUserPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission: string): boolean => {
    return userPermissions.includes(permission);
  }, [userPermissions]);

  // Fetch permissions when user authenticates and SDK is ready
  useEffect(() => {
    // Wait for Auth0 to finish loading before checking authentication
    if (authLoading) {
      return;
    }

    // Wait for SDK to be ready before making API calls
    if (isAuthenticated && !isSdkReady) {
      return;
    }

    if (isAuthenticated && isSdkReady) {
      fetchUserPermissions().then(data => {
        if (data) {
          setUserPermissions(data.permissions);
          setUserRole(data.role);
        }
        setPermissionsLoadingInternal(false);
      });
    } else if (!isAuthenticated) {
      setUserPermissions([]);
      setUserRole(null);
      setPermissionsLoadingInternal(false);
      permissionsCache.current = null;
    }
  }, [isAuthenticated, authLoading, isSdkReady, fetchUserPermissions]);

  const value = useMemo<AppCacheContextValue>(() => ({
    userPermissions,
    userRole,
    hasPermission,
    permissionsLoading,
    refreshPermissions,
  }), [userPermissions, userRole, hasPermission, permissionsLoading, refreshPermissions]);

  return (
    <AppCacheContext.Provider value={value}>
      {children}
    </AppCacheContext.Provider>
  );
}

export function useAppCache(): AppCacheContextValue {
  const context = useContext(AppCacheContext);
  if (!context) {
    throw new Error('useAppCache must be used within an AppCacheProvider');
  }
  return context;
}
